import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PhoneCall } from "lucide-react";
import { API, AUDIENCES, COMPANY } from "@/data/content";

const TIMES = ["Any time", "Morning (9–12)", "Afternoon (12–3)", "Evening (3–6)"];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-4 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold transition-all duration-300";

export default function Callback() {
  const [params] = useSearchParams();
  const preset = params.get("audience");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    audience: AUDIENCES.some((a) => a.slug === preset) ? preset : "general",
    preferred_time: "Any time",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const valid = form.name.trim().length >= 2 && form.phone.trim().length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/callbacks`, {
        ...form,
        source: params.get("utm_source") || params.get("source") || "website",
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="callback-page" className="relative min-h-screen bg-obsidian grain flex items-center">
      <div className="absolute -top-32 left-1/4 w-[28rem] h-[28rem] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-[24rem] h-[24rem] rounded-full bg-brand/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-xl w-full mx-auto px-4 sm:px-6 pt-32 pb-20">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-gold" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Request a callback</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white leading-[1.05]">
          Talk to Anu <span className="font-editorial italic font-medium text-gold">today.</span>
        </h1>
        <p className="mt-5 text-white/55 leading-relaxed">
          Thirty seconds now, a call back at a time that suits you. No obligation, no jargon — just a straight answer
          on what's possible for your scenario.
        </p>

        {done ? (
          <div data-testid="callback-success" className="mt-10 rounded-3xl bg-white/5 border border-gold/30 p-8 sm:p-10 text-center">
            <CheckCircle2 size={44} className="text-gold mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-bold text-white">Request received.</h2>
            <p className="mt-3 text-sm text-white/55 leading-relaxed max-w-xs mx-auto">
              Thanks {form.name.split(" ")[0]} — Anu will call {form.phone} {form.preferred_time === "Any time" ? "as soon as possible" : `in the ${form.preferred_time.split(" ")[0].toLowerCase()}`}.
            </p>
            <p className="mt-6 text-xs text-white/35">
              Urgent? Call now: <a href={`tel:${COMPANY.phone}`} className="text-gold hover:underline">{COMPANY.phoneDisplay}</a>
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="callback-form" className="mt-10 rounded-3xl bg-white/5 border border-white/10 p-6 sm:p-8 space-y-5 backdrop-blur-sm">
            <input data-testid="callback-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} />
            <input data-testid="callback-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Best number to call" className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <select data-testid="callback-audience-select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className={`${inputCls} appearance-none [&>option]:text-ink`}>
                <option value="general">I am…</option>
                {AUDIENCES.map((a) => (
                  <option key={a.slug} value={a.slug}>{a.title}</option>
                ))}
              </select>
              <select data-testid="callback-time-select" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className={`${inputCls} appearance-none [&>option]:text-ink`}>
                {TIMES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <textarea data-testid="callback-message-input" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What do you need funding for? (optional)" className={`${inputCls} resize-none`} />
            <button
              type="submit"
              data-testid="callback-submit"
              disabled={!valid || sending}
              className="w-full rounded-full bg-gold text-obsidian px-8 py-4 text-sm font-bold tracking-wide transition-all duration-300 hover:shadow-gold-glow disabled:opacity-40 inline-flex items-center justify-center gap-3"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <PhoneCall size={16} />}
              Call me back
            </button>
            <p className="text-[11px] text-white/30 leading-relaxed text-center">
              Handled under our <Link to="/privacy" className="underline hover:text-gold">Privacy Policy</Link>. Used only to return your call.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
